import React, { useEffect, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import MainCompanyController from '../Controllers/MainCompanyController';
import apiClient from '../Utils/apiClient';
import { toastSuccess, toastError } from '../Utils/toast';

const EMPTY_FORM = { id: null, name: '', short_name: '', description: '', status: true };

function CredentialField({ label, value, onCopy, onRegenerate, regenerating }) {
  return (
    <div className="d-flex align-items-center" style={{ gap: 6 }}>
      <span className="text-muted" style={{ fontSize: 12, minWidth: 62 }}>{label}</span>
      <code style={{ fontSize: 12, background: 'var(--ivs-bg)', padding: '2px 6px', borderRadius: 4 }}>
        {value || '—'}
      </code>
      {value && (
        <button type="button" className="ivs-icon-btn" style={{ width: 24, height: 24 }} aria-label={`Copy ${label}`} onClick={onCopy}>
          <i className="bi bi-clipboard" style={{ fontSize: 12 }}></i>
        </button>
      )}
      <button type="button" className="ivs-icon-btn" style={{ width: 24, height: 24 }} aria-label={`Regenerate ${label}`} disabled={regenerating} onClick={onRegenerate}>
        <i className={`bi ${regenerating ? 'bi-hourglass-split' : 'bi-arrow-repeat'}`} style={{ fontSize: 12 }}></i>
      </button>
    </div>
  );
}

export default function MainCompanies() {
  const { auth } = useAuth();
  const canCreate = auth?.role === 'SuperAdmin';
  const canDelete = auth?.role === 'SuperAdmin';
  const canManageCredentials = auth?.role === 'SuperAdmin' || auth?.role === 'Admin';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await MainCompanyController.list();
      setList(data || []);
    } catch (err) {
      setError('Failed to load main companies. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreateForm = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (item) => {
    setForm({
      id: item.id,
      name: item.name,
      short_name: item.short_name || '',
      description: item.description || '',
      status: item.status,
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toastError('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        short_name: form.short_name || null,
        description: form.description || null,
        status: form.status,
      };
      if (form.id) {
        await MainCompanyController.update(form.id, payload);
        toastSuccess('Main company updated');
      } else {
        await MainCompanyController.create(payload);
        toastSuccess('Main company created');
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      fetchList();
    } catch (err) {
      console.error('Failed to save main company', err);
      const msg = Array.isArray(err.response?.data?.data)
        ? err.response.data.data.map((e) => e.message).join(', ')
        : err.response?.data?.data || 'Failed to save main company';
      toastError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this main company?',
      text: 'Companies linked to it will be un-linked, not deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await MainCompanyController.remove(id);
      toastSuccess('Main company deleted');
      if (expandedId === id) setExpandedId(null);
      fetchList();
    } catch (err) {
      console.error('Failed to delete main company', err);
      toastError(err.response?.data?.data || 'Failed to delete main company');
    }
  };

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toastSuccess(`${label} copied`);
    } catch (err) {
      toastError(`Failed to copy ${label}`);
    }
  };

  const regenerateCredential = async (companyId, field) => {
    const label = field === 'client_id' ? 'Client ID' : 'API key';
    const result = await Swal.fire({
      title: `Regenerate ${label}?`,
      text: `The old ${label} will stop working immediately. Any integration using it will need to be updated.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Regenerate',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    const key = `${companyId}:${field}`;
    setRegeneratingKey(key);
    try {
      const endpoint = field === 'client_id' ? 'regenerate-client-id' : 'regenerate-apikey';
      const response = await apiClient.post(`companies/${companyId}/${endpoint}`);
      const newValue = response.data.data[field];
      setLinkedCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, [field]: newValue } : c)));
      toastSuccess(`${label} regenerated`);
    } catch (err) {
      console.error('Failed to regenerate credential', err);
      toastError(err.response?.data?.data || 'Failed to regenerate credential');
    } finally {
      setRegeneratingKey('');
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    setLinkedLoading(true);
    try {
      const data = await MainCompanyController.getCompanies(id);
      setLinkedCompanies(data.companies || []);
    } catch (err) {
      console.error('Failed to load linked companies', err);
      toastError('Failed to load linked companies');
    } finally {
      setLinkedLoading(false);
    }
  };

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">Main Companies</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              Optional parent grouping above individual companies (e.g. a reseller with several accounts underneath)
            </p>
          </div>

          {canCreate && (
            <button className="ivs-btn-primary" type="button" onClick={openCreateForm}>
              <i className="bi bi-plus-lg mr-1"></i> Add Main Company
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        {formOpen && (
          <form className="ivs-search-panel" onSubmit={handleSubmit}>
            <h3 className="ivs-section-title" style={{ marginBottom: 16 }}>
              {form.id ? 'Edit Main Company' : 'New Main Company'}
            </h3>

            <div className="ivs-search-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 16 }}>
              <div>
                <label className="ivs-field-label">Name</label>
                <input
                  className="ivs-field"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Acme Group"
                  required
                />
              </div>
              <div>
                <label className="ivs-field-label">Short Name</label>
                <input
                  className="ivs-field"
                  value={form.short_name}
                  onChange={(e) => setForm((p) => ({ ...p, short_name: e.target.value }))}
                  placeholder="e.g. ACME"
                />
              </div>
              <div>
                <label className="ivs-field-label">Status</label>
                <select
                  className="ivs-field"
                  value={form.status ? 'true' : 'false'}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value === 'true' }))}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="ivs-field-label">Description</label>
              <textarea
                className="ivs-field"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional notes about this grouping"
              />
            </div>

            <div className="d-flex" style={{ gap: 10 }}>
              <button className="ivs-btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create Main Company'}
              </button>
              <button
                type="button"
                className="ivs-pagination__btn"
                onClick={() => { setFormOpen(false); setForm(EMPTY_FORM); }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="ivs-panel" style={{ padding: 0 }}>
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Short Name</th>
                <th>Status</th>
                <th>Options</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center">Loading…</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan="5" className="text-center">No main companies found</td></tr>
              ) : (
                list.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="ivs-icon-btn"
                          onClick={() => toggleExpand(item.id)}
                          aria-label="View linked companies"
                        >
                          <i className={`bi ${expandedId === item.id ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                        </button>
                      </td>
                      <td>{item.name}</td>
                      <td>{item.short_name || '—'}</td>
                      <td>
                        {item.status
                          ? <span className="badge badge-success">Active</span>
                          : <span className="badge badge-danger">Inactive</span>}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="mr-3 btn btn-rounded btn-icon p-0 w-auto h-auto"
                          onClick={() => openEditForm(item)}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="btn btn-rounded btn-icon p-0 w-auto h-auto"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === item.id && (
                      <tr>
                        <td></td>
                        <td colSpan="4" style={{ background: 'var(--ivs-bg)' }}>
                          {linkedLoading ? (
                            <div className="text-muted" style={{ fontSize: 13, padding: '8px 0' }}>Loading companies…</div>
                          ) : linkedCompanies.length === 0 ? (
                            <div className="text-muted" style={{ fontSize: 13, padding: '8px 0' }}>No companies linked yet</div>
                          ) : (
                            <div style={{ padding: '8px 0' }}>
                              {linkedCompanies.map((c) => (
                                <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--ivs-border)' }}>
                                  <div className="d-flex align-items-center justify-content-between" style={{ fontSize: 13, marginBottom: 6 }}>
                                    <span>{c.name_firma} ({c.short_name})</span>
                                    <span className={c.status ? 'badge badge-success' : 'badge badge-danger'}>
                                      {c.status ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                  {canManageCredentials && (
                                    <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                                      <CredentialField
                                        label="Client ID"
                                        value={c.client_id}
                                        onCopy={() => copyValue(c.client_id, 'Client ID')}
                                        onRegenerate={() => regenerateCredential(c.id, 'client_id')}
                                        regenerating={regeneratingKey === `${c.id}:client_id`}
                                      />
                                      <CredentialField
                                        label="API Key"
                                        value={c.apikey}
                                        onCopy={() => copyValue(c.apikey, 'API Key')}
                                        onRegenerate={() => regenerateCredential(c.id, 'apikey')}
                                        regenerating={regeneratingKey === `${c.id}:apikey`}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
